import React, { useState, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { translateBlock } from 'i18n';
import { useTranslation } from 'react-i18next';
import { DropzoneArea } from 'mui-file-dropzone';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { getColumnType, RawLine } from 'routes/Cellar/helpers';
import {
    AvailableTable,
    PreviewColumnT,
    PreviewData,
} from 'routes/Cellar/types';
import { Step } from 'routes/ImportCsv/types';

import Alert from 'ui/Alert/Alert';
import Loading from 'components/Loading';
import {
    getSurplusCSVColumnLabels,
    isCSVEmpty,
    isNotACSV,
    getDuplicatedColumns,
    ColumnTableT,
    getCsvEncoding,
    convertFileToWindows1252,
    isContainingExpectedColumns,
    getExpectedColumnsNotFoundInFile,
} from 'routes/ImportCsv/ImportWorkflow/csvValidators';
import { downloadModel } from 'routes/ImportCsv/ImportWorkflow/downloadDataHelpers';
import DownloadDataAlert from 'routes/ImportCsv/ImportWorkflow/components/DownloadDataAlert';
import { useNotifyCSVMessage } from 'routes/ImportCsv/ImportWorkflow/hooks/csvSnackbar';
import { useParseCsvForPreview } from 'routes/ImportCsv/ImportWorkflow/hooks/preview';
import { useIsV1Table } from 'routes/ImportCsv/ImportWorkflow/hooks/helpers';
import Flex from 'ui/Flex/Flex';
import { Box } from '@mui/material';

const FILE_SIZE_LIMIT = 10000000;
const MAX_COLUMN_NAME_LENGTH = 63;

type Props = {
    selectedTable: AvailableTable | null;
    initialStep: Step;
    setStep(step: Step): void;
    tableData: GetDataResponse;
    isNewTable: boolean;
    setRawfile(file: File): void;
    setPrevizData(file: PreviewData): void;
};

const SelectFileStep = ({
    selectedTable,
    initialStep,
    setStep,
    tableData,
    isNewTable,
    setRawfile,
    setPrevizData,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const [filename, setFilename] = useState<string>('');
    const [fileLength, setFileLength] = useState<number>(0);
    const errorSnackbarNotifCallback = useNotifyCSVMessage('error');
    const warningSnackbarNotifCallback = useNotifyCSVMessage('warning');
    const successSnackbarNotifCallback = useNotifyCSVMessage('success');

    const isDataInTable = useMemo(
        (): boolean =>
            Array.isArray(tableData?.data) && tableData.data.length > 0,
        [tableData.data],
    );
    const isV1Table = useIsV1Table(selectedTable);

    const downloadPreviousData = useCallback(() => {
        const fileName: string =
            selectedTable || t('importCSV.defaultFileName');
        downloadModel(fileName, tableData.columns, tableData.data);
    }, [selectedTable, tableData.columns, tableData.data]);

    const handleNotACSVFile = useCallback((fileName: string): void => {
        const message = t('cellar.rejectedType', { fileName });

        return errorSnackbarNotifCallback({ message });
    }, []);

    const handleEmptyCSV = useCallback(() => {
        const message = t('importCSV.error.emptyCSV');
        errorSnackbarNotifCallback({ message });
    }, []);

    const handleDuplicatedColumns = useCallback(
        (duplicatedColumns: string[]) => {
            const message = t('importCSV.error.duplicatedColumns', {
                duplicatedColumns,
            });
            errorSnackbarNotifCallback({ message });
        },
        [],
    );

    const handleLongColumnName = useCallback((longColumnNames: string[]) => {
        const message = t('importCSV.error.longColumnName', {
            longColumnNames,
            maxLength: MAX_COLUMN_NAME_LENGTH,
        });
        errorSnackbarNotifCallback({ message });
    }, []);

    const notifyMoreColumnsThanExpected = useCallback(
        (surplusColumnsLabel: string[]) => {
            const message = t('importCSV.warning.surplusColumns', {
                surplusColumnsLabel: surplusColumnsLabel.join(', '),
            });
            warningSnackbarNotifCallback({ message });
        },
        [],
    );

    const notifySuccessOrSurplusColumnWarning = useCallback(
        (
            isInitializingTable: boolean,
            expectedColumns: ColumnTableT[],
            fileColumns: PreviewColumnT[],
        ): void => {
            if (isInitializingTable) {
                return successSnackbarNotifCallback({
                    message: t('importCSV.success.fileParsed'),
                });
            }
            const expectedColumnNames = expectedColumns.map((col) => col.label);
            const fileColumnNames = fileColumns.map((col) => col.label);
            const surplusColumnLabels = getSurplusCSVColumnLabels({
                expectedColumnNames,
                fileColumnNames,
            });
            if (surplusColumnLabels.length > 0)
                return notifyMoreColumnsThanExpected(surplusColumnLabels);

            return successSnackbarNotifCallback({
                message: t('importCSV.success.fileParsed'),
            });
        },
        [],
    );
    const onParsingPreviewSuccess = useCallback(
        (data: PreviewData) => {
            const { columns, rows, nbRows } = data;
            const previewColumn = columns
                .filter((column) => {
                    if (!isNewTable) {
                        const tableColumn = tableData.columns.find(
                            (col) => col.label === column.label,
                        );
                        return tableColumn !== undefined;
                    }
                    return true;
                })
                .map((column) => ({
                    ...column,
                    toImport: true,
                }));
            setPrevizData({
                name: filename,
                rows,
                columns: previewColumn,
                nbRows,
                fileLength,
            });
            notifySuccessOrSurplusColumnWarning(
                tableData?.columns.length === 0,
                tableData.columns,
                columns,
            );
            setStep('previzTableStep');
        },
        [
            filename,
            fileLength,
            setPrevizData,
            setStep,
            notifySuccessOrSurplusColumnWarning,
            tableData,
        ],
    );
    const parseCsvForPreview = useParseCsvForPreview(
        isNewTable,
        onParsingPreviewSuccess,
        errorSnackbarNotifCallback,
    );

    const handleWrongColumns = useCallback(
        (wrongColumnsNames: string[], encoding: string) => {
            const isEncodingProbablyWrong = ![
                'utf-8',
                'utf8',
                'windows-1252',
                'cp1252',
            ].includes(encoding);
            let message: string;
            const formattedColumns = wrongColumnsNames
                .slice(0, 3)
                .map((c) => `"${c}"`)
                .join(', ');
            const suffix = wrongColumnsNames.length > 3 ? '...' : '';
            const colsWithSuffix = `${formattedColumns}${suffix}`;
            if (isEncodingProbablyWrong) {
                message = t('importCSV.error.wrongColumnsWithEncoding', {
                    columns: colsWithSuffix,
                    encoding,
                });
            } else {
                message = t('importCSV.error.wrongColumns', {
                    columns: colsWithSuffix,
                });
            }
            errorSnackbarNotifCallback({ message });
            parseCsvForPreview.reset();
        },
        [],
    );

    const handleMissingColumns = useCallback((missingColumnNames: string[]) => {
        const message = t('importCSV.error.missingColumns', {
            columns: missingColumnNames.join(', '),
        });
        errorSnackbarNotifCallback({ message });
        parseCsvForPreview.reset();
    }, []);

    const analyzeData = async (newFiles: File[]): Promise<void> => {
        if (!newFiles.length) return;
        const [file] = newFiles;
        if (isNotACSV(file)) {
            handleNotACSVFile(file.name);
            return;
        }
        setRawfile(file);
        setFilename(file.name);
        const encoding = (await getCsvEncoding(file)) ?? 'ascii';
        const shouldConvertEncoding =
            encoding &&
            !['utf8', 'utf-8', 'cp1252', 'windows-1252'].includes(
                encoding.toLowerCase(),
            );
        const fileToParse = shouldConvertEncoding
            ? await convertFileToWindows1252(file)
            : file;
        const { parse } = await import('papaparse');
        parse<RawLine, File>(fileToParse, {
            header: true,
            complete: ({ data, meta }) => {
                if (isCSVEmpty(data)) {
                    handleEmptyCSV();
                    return;
                }

                const duplicatedColumns = meta.fields
                    ? getDuplicatedColumns(meta.fields)
                    : [];
                if (duplicatedColumns.length > 0) {
                    handleDuplicatedColumns(duplicatedColumns);
                    return;
                }

                const longColumnNames = meta.fields
                    ? meta.fields.filter(
                          (field) => field.length > MAX_COLUMN_NAME_LENGTH,
                      )
                    : [];
                if (longColumnNames.length > 0) {
                    handleLongColumnName(longColumnNames);
                    return;
                }
                const columns = _(data[0])
                    .keys()
                    .map((header) => ({
                        name: header,
                        key: header,
                        title: isV1Table ? translateBlock(header) : header,
                        type: getColumnType(header, data),
                    }))
                    .value();
                const isInitializingTable = tableData?.columns === undefined;

                if (!isInitializingTable) {
                    const tableColumnNames = tableData.columns.map(
                        (col) => col.label,
                    );
                    const fileColumnNames = columns.map((col) => col.name);
                    const shouldAbort = !isContainingExpectedColumns({
                        expectedColumnNames: tableColumnNames,
                        fileColumnNames,
                    });
                    if (shouldAbort) {
                        const probablyWrongNamedColumns =
                            getSurplusCSVColumnLabels({
                                expectedColumnNames: tableColumnNames,
                                fileColumnNames,
                            });
                        // we need to check surplus columns first, because missing columns could actually be badly named columns
                        if (probablyWrongNamedColumns.length === 0) {
                            const missingColumns =
                                getExpectedColumnsNotFoundInFile({
                                    expectedColumnNames: tableColumnNames,
                                    fileColumnNames,
                                });
                            handleMissingColumns(missingColumns);
                        } else {
                            handleWrongColumns(
                                probablyWrongNamedColumns,
                                encoding,
                            );
                        }

                        return;
                    }
                }
                setFileLength(data.length);
                parseCsvForPreview.mutate(file);
            },
        });
    };

    return (
        <Flex direction="column">
            <Flex align="center" justify="space-between">
                <Typography variant="h5">
                    {t('importCSV.addingFile')}
                </Typography>

                {!!selectedTable && (
                    <Button
                        onClick={() =>
                            tableData &&
                            tableData.columns &&
                            downloadModel(
                                `model ${selectedTable}`,
                                tableData.columns,
                                tableData.data.slice(0, 5),
                            )
                        }
                        variant="outlined"
                        disabled={!tableData || !tableData.columns}
                        style={{ alignSelf: 'flex-end' }}
                    >
                        {t('importCSV.downloadModel')}
                    </Button>
                )}
            </Flex>

            {isDataInTable && (
                <DownloadDataAlert download={downloadPreviousData} />
            )}
            {!parseCsvForPreview.isPending ? (
                <Box sx={{ mt: '1rem' }}>
                    <DropzoneArea
                        onChange={analyzeData}
                        maxFileSize={FILE_SIZE_LIMIT}
                        fileObjects={[]}
                        alertSnackbarProps={{
                            anchorOrigin: {
                                horizontal: 'center',
                                vertical: 'bottom',
                            },
                        }}
                        getDropRejectMessage={(file) =>
                            t('importCSV.error.fileTooBig', {
                                maxSize: FILE_SIZE_LIMIT / 1000000,
                                fileSize: file.size / 1000000,
                            })
                        }
                        filesLimit={1}
                        dropzoneText={t('cellar.dropzoneText')}
                        showAlerts={['error']}
                    />
                </Box>
            ) : (
                <Flex
                    align="center"
                    justify="center"
                    direction="column"
                    sx={{ minHeight: '250px' }}
                >
                    <Loading />
                    <Typography variant="body1">
                        {t('importCSV.loadingPreview')}
                    </Typography>
                </Flex>
            )}

            <Box sx={{ alignSelf: 'flex-end', my: '1rem' }}>
                {initialStep !== 'selectFileStep' && (
                    <Button
                        onClick={() => {
                            setStep('showTableStep');
                        }}
                        variant="outlined"
                        title={t('common.deleteConfirm')}
                    >
                        {t('common.cancel')}
                    </Button>
                )}
            </Box>

            <Alert severity="info" icon={false} action={null}>
                <b>{t('importCSV.tips.header')}</b>
                <br />
                <ul>
                    <li>{t('importCSV.tips.csvFormatTip')}</li>
                    <li>{t('importCSV.tips.dateFormatTip')}</li>
                    <li>{t('importCSV.tips.unitsTip')}</li>
                    <li>{t('importCSV.tips.duplicatedColumnNameTip')}</li>
                    {isNewTable && (
                        <li>
                            {t('importCSV.tips.csvColumnNameTip', {
                                maxLength: MAX_COLUMN_NAME_LENGTH,
                            })}
                        </li>
                    )}
                </ul>
            </Alert>
        </Flex>
    );
};

export default SelectFileStep;
